import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';
import { AUTO_LOAD_CONTROL } from '../config-control';
import {
  BACKGROUND_CREATE_MODE,
  BATCH_APPROVAL_MODE,
  CONSULT_MODE,
  CREATE_MODE,
  NONE_BACKGROUND_MODE,
  NONE_MODE,
  NONE_SUB_MODE,
  SUB_CONSULT_MODE,
  SUB_CREATE_MODE,
  SUB_UPDATE_MODE,
  UPDATE_MODE,
} from '../mode';
import { NO_REFRESH, REFRESH } from '../refresh';
import {
  ALTERNATIVE_LOADING_STATUS,
  FAILURE_STATUS,
  IDLE_STATUS,
  LOADING_STATUS,
  SUCCESS_STATUS,
} from '../status';
import {
  fetchConciliacaoPendenteAllAsync,
  generateReportByDateAsync,
  fetchAllAsync,
  fetchAllViewAsync,
} from './conciliacaoBancariaThunks';

import i18n from '../../i18n';

const initialState = {
  status: IDLE_STATUS,
  mode: {
    main: NONE_MODE,
    background: NONE_BACKGROUND_MODE,
    sub: NONE_SUB_MODE,
  },
  type: '',
  refresh: NO_REFRESH,
  error: null,
  response: {
    status: null,
    message: null,
    data: null,
  },
  config: {
    control: AUTO_LOAD_CONTROL,
  },
  list: [],
  view:[],
  model: {
  },
  related: {
    model: {
      report: {},
    },
    list: {
      selectionConciliacaoBancaria: [],
    },
  },
};

const conciliacaoBancariaSlice = createSlice({
  name: 'conciliacaoBancaria',
  initialState,
  reducers: {
    changeStatusTo: (state, action) => {
      state.status = action.payload.status;
    },
    loading: (state) => {
      state.status = LOADING_STATUS;
    },
    success: (state) => {
      state.status = SUCCESS_STATUS;
    },
    failure: (state) => {
      state.status = FAILURE_STATUS;
    },
    resetStatus: (state) => {
      state.status = IDLE_STATUS;
    },
    refresh: (state) => {
      state.refresh = REFRESH;
    },
    resetRefresh: (state) => {
      state.refresh = NO_REFRESH;
    },
    changeToCreateMode: (state) => {
      state.mode.main = CREATE_MODE;
    },
    changeToConsultMode: (state) => {
      state.mode.main = CONSULT_MODE;
    },
    changeToUpdateMode: (state) => {
      state.mode.main = UPDATE_MODE;
    },
    changeToBatchApprovalMode: (state) => {
      state.mode.main = BATCH_APPROVAL_MODE;
    },
    resetMode: (state) => {
      state.mode.main = NONE_MODE;
    },
    changeToBackgroundCreateMode: (state) => {
      state.mode.background = BACKGROUND_CREATE_MODE;
    },
    resetBackgroundMode: (state) => {
      state.mode.background = NONE_BACKGROUND_MODE;
    },
    changeToSubCreateMode: (state) => {
      state.mode.sub = SUB_CREATE_MODE;
      state.related.model.contato = {};
    },
    changeToSubConsultMode: (state) => {
      state.mode.sub = SUB_CONSULT_MODE;
    },
    changeToSubUpdateMode: (state) => {
      state.mode.sub = SUB_UPDATE_MODE;
    },
    resetSubMode: (state) => {
      state.mode.sub = NONE_SUB_MODE;
    },
    changeControlTo: (state, action) => {
      state.config.control = action.payload;
    },
    resetControl: (state) => {
      state.config.control = initialState.config.control;
    },
    setModel: (state, action) => {
      const updatedContatosList = action.payload.contatos.map(
        (current) => ((current?.id === null)
          ? {
            ...current,
            id: uuid(),
          }
          : current
        )
      );

      state.model = {
        ...action.payload,
        contatos: updatedContatosList,
      };
    },
    addToList: (state, action) => {
      state.list = [...state.list, action.payload.data];
    },
    updateOnList: (state, action) => {
      state.list = state.list.map((current) => {
        if (current.id === action.payload.data?.id) {
          return action.payload.data;
        }
        return current;
      });
    },
    setList: (state, action) => {
      state.list = action.payload;
    },
    setRelatedSelectionConciliacaoBancariaList: (state, action) => {
      state.related.list.selectionConciliacaoBancaria = action.payload;
    },
    resetRelatedSelectionConciliacaoBancariaList: (state) => {
      state.related.list.selectionConciliacaoBancaria = (
        initialState.related.list.selectionConciliacaoBancaria
      );
    },
    setResponse: (state, action) => {
      state.response = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearResponse: (state) => {
      state.response = {
        status: null,
        message: null,
        data: null,
      };
    },
    clearError: (state) => {
      state.error = initialState.error;
    },
    resetModel: (state) => {
      state.model = initialState.model;
    },
    resetList: (state) => {
      state.list = initialState.list;
    },
    setRelatedReportModel: (state, action) => {
      state.related.model.report = {
        ...action.payload,
      };
    },
    resetRelatedReportModel: (state) => {
      state.related.model.report = initialState.related.model.report;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConciliacaoPendenteAllAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchConciliacaoPendenteAllAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchConciliacaoPendenteAllAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaConciliacaoPendenciaBancaria', { erro: action.error.message }),
        };
        state.refresh = NO_REFRESH;
      })
      .addCase(fetchAllAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchAllAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaConciliacaoBancaria', { erro: action.error.message }),
        };
        state.refresh = NO_REFRESH;
      })
      .addCase(fetchAllViewAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllViewAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.view = action.payload.data;
      })
      .addCase(fetchAllViewAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaConciliacaoBancaria', { erro: action.error.message }),
        };
        state.refresh = NO_REFRESH;
      })
      .addCase(generateReportByDateAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(generateReportByDateAsync.fulfilled, (state) => {
        state.status = SUCCESS_STATUS;
      })
      .addCase(generateReportByDateAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroGerarRelatorioConciliacaoBancaria', { erro: action.error.message }),
        };
      });
  },
});

const {
  changeStatusTo,
  loading,
  success,
  failure,
  resetStatus,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  changeToBatchApprovalMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  changeToSubCreateMode,
  changeToSubConsultMode,
  changeToSubUpdateMode,
  resetSubMode,
  changeControlTo,
  resetControl,
  refresh,
  resetRefresh,
  setModel,
  resetModel,
  addToList,
  setList,
  resetList,
  setRelatedSelectionConciliacaoBancariaList,
  resetRelatedSelectionConciliacaoBancariaList,
  updateOnList,
  setRelatedContatoModel,
  addContato,
  updateContato,
  setResponse,
  setResponseMessage,
  clearResponse,
  setError,
  clearError,
  resetContatos,
  setRelatedReportModel,
  resetRelatedReportModel,
} = conciliacaoBancariaSlice.actions;

const conciliacaoBancariaActions = {
  ...conciliacaoBancariaSlice.actions,
};

export {
  conciliacaoBancariaSlice,
  changeStatusTo,
  loading,
  success,
  failure,
  resetStatus,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  changeToBatchApprovalMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  changeToSubCreateMode,
  changeToSubConsultMode,
  changeToSubUpdateMode,
  resetSubMode,
  changeControlTo,
  resetControl,
  refresh,
  resetRefresh,
  setModel,
  resetModel,
  addToList,
  setList,
  resetList,
  setRelatedSelectionConciliacaoBancariaList,
  resetRelatedSelectionConciliacaoBancariaList,
  updateOnList,
  setRelatedContatoModel,
  addContato,
  updateContato,
  resetContatos,
  setResponse,
  setResponseMessage,
  clearResponse,
  setError,
  clearError,
  setRelatedReportModel,
  resetRelatedReportModel,
  conciliacaoBancariaActions,
};

export default conciliacaoBancariaSlice.reducer;
